import { useEffect } from 'react';
import L from 'leaflet';
import { TileLayer, useMap } from 'react-leaflet';
import { getMapAttribution, getMapTileUrl } from '../../styles/themes';

/**
 * The whole world as Web Mercator can draw it (#576).
 *
 * The projection is square at ±85.0511°; beyond that there are no tiles, which
 * is where the grey bands above the Arctic and below Antarctica came from.
 */
export const WORLD_BOUNDS = L.latLngBounds(
  [-85.0511287798, -180],
  [85.0511287798, 180]
);

/**
 * `MapContainer` options every map in the app is built with (#576): panning
 * stops at the world's edge rather than merely resisting it.
 */
export const WORLD_MAP_OPTIONS = {
  maxBounds: WORLD_BOUNDS,
  maxBoundsViscosity: 1.0,
} as const;

/**
 * The lowest zoom at which the world covers the whole map, in both directions.
 *
 * Not `map.getBoundsZoom(WORLD_BOUNDS, true)`: that clamps its answer to the
 * map's current minimum zoom, so once raised for a tall window it could never
 * come back down for a short one. Returns null while the map has no size yet.
 */
const worldFillingZoom = (map: L.Map): number | null => {
  const size = map.getSize();
  if (size.x === 0 || size.y === 0) {
    return null;
  }
  const worldAtZoomZero = map
    .project(WORLD_BOUNDS.getNorthEast(), 0)
    .subtract(map.project(WORLD_BOUNDS.getSouthWest(), 0));
  const scale = Math.max(
    size.x / Math.abs(worldAtZoomZero.x),
    size.y / Math.abs(worldAtZoomZero.y)
  );
  return Math.ceil(map.getScaleZoom(scale, 0));
};

/**
 * The basemap, drawn once, with the minimum zoom kept at the level where the
 * world fills the map (#576).
 *
 * One copy of the world (`noWrap`), so each drone is drawn once. The minimum
 * zoom depends on the map's size, so it is recomputed whenever the container
 * resizes — a phone, a tall monitor, or the fleet map shrinking when the drone
 * panel opens beside it, which changes no window size and so is invisible to
 * Leaflet's own resize tracking.
 *
 * Must live inside a `MapContainer` constructed with `WORLD_MAP_OPTIONS`.
 */
export const WorldBasemap: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const keepWorldFilling = () => {
      const zoom = worldFillingZoom(map);
      if (zoom !== null) {
        map.setMinZoom(zoom);
      }
    };
    keepWorldFilling();
    // invalidateSize fires 'resize' when the size really changed.
    map.on('resize', keepWorldFilling);

    // jsdom has no ResizeObserver; every browser the UI supports does.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => map.invalidateSize());
    observer?.observe(map.getContainer());

    return () => {
      observer?.disconnect();
      map.off('resize', keepWorldFilling);
    };
  }, [map]);

  return (
    <TileLayer
      url={getMapTileUrl()}
      attribution={getMapAttribution()}
      maxZoom={19}
      noWrap
    />
  );
};
